/**
 * precio.js — cómo se lee y se pinta el precio de un plan del bloque «Precios» (`blocks/pricing.js`).
 *
 * Módulo PURO (sin DOM ni `window`), ES2019 (sin `?.` ni `??`): lo importa el render y se prueba en node
 * (tests/_precio.mjs).
 *
 * Por qué existe (17-10-2026, decisión del dueño con la lámina `2026-10-17_precios-dolar.png`): el bloque
 * pintaba un «$» FIJO delante de cada precio y declaraba `priceCurrency=USD` a Google. Los precios los
 * escribe la IA o el cliente con su divisa o en texto, así que lo publicado decía «$ Desde 85€ / día»,
 * «$ 599€» o «$ Consultar» (los 48 precios de test222). Ahora:
 *   · el precio se pinta TAL COMO ESTÁ ESCRITO;
 *   · un número a secas («29») lleva «€» detrás, que pinta el render FUERA del campo editable (el dato
 *     guardado no cambia);
 *   · la microdata dice la divisa que se ve (€ → EUR, $ → USD, £ → GBP; el número a secas, EUR) y, si
 *     no se ve ninguna, no se emite (como `views._seo_precio`: una Offer con moneda equivocada es peor
 *     que no emitirla);
 *   · la cifra se lee a la española: «1.200» son mil doscientos y «1.299,99», mil doscientos y pico.
 */

/** Divisa que se pinta detrás de un número a secas. */
export var DIVISA_POR_DEFECTO = '€';

var MONEDAS = { '€': 'EUR', 'EUR': 'EUR', '$': 'USD', 'USD': 'USD', '£': 'GBP', 'GBP': 'GBP' };
var RE_SIMBOLO = /€|\$|£|\b(?:EUR|USD|GBP)\b/i;
var RE_CIFRA = /\d[\d.,]*/;

/** Texto plano del precio (el campo es un contenteditable: puede traer marcado y entidades). */
function plano(texto) {
    if (texto == null) return '';
    return String(texto)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&euro;/gi, '€')
        .replace(/&pound;/gi, '£')
        .replace(/&amp;/gi, '&')
        .trim();
}

/** ¿El precio es solo una cifra («29», «9,50», «1.200»), sin divisa ni texto? */
export function esSoloNumero(texto) {
    return /^\d[\d.,\s]*$/.test(plano(texto)) && /\d$/.test(plano(texto));
}

/** Símbolo de divisa que se VE en el precio, normalizado ('€' | '$' | '£' | 'EUR'…), o ''. */
export function simboloDePrecio(texto) {
    var m = plano(texto).match(RE_SIMBOLO);
    return m ? (m[0].length > 1 ? m[0].toUpperCase() : m[0]) : '';
}

/**
 * La PRIMERA cifra del precio como número, leída a la española, o null.
 *   «1.200 €» → 1200 · «1.299,99 €» → 1299.99 · «9,50» → 9.5 · «$9.50» → 9.5 · «1,299.99» → 1299.99
 * Con los dos separadores, el ÚLTIMO es el decimal. Con uno solo, es decimal si lo siguen 1-2 cifras al
 * final; si no, es de miles («1.200», «12,000»).
 */
export function numeroDePrecio(texto) {
    var m = plano(texto).match(RE_CIFRA);
    if (!m) return null;
    var s = m[0].replace(/[.,]+$/, '');
    var punto = s.lastIndexOf('.');
    var coma = s.lastIndexOf(',');
    var dec = '';
    if (punto >= 0 && coma >= 0) dec = punto > coma ? '.' : ',';
    else if (punto >= 0 || coma >= 0) {
        var sep = punto >= 0 ? '.' : ',';
        if (s.split(sep).length === 2 && /^\d{1,2}$/.test(s.slice(s.lastIndexOf(sep) + 1))) dec = sep;
    }
    var entero = dec ? s.slice(0, s.lastIndexOf(dec)) : s;
    var decimales = dec ? s.slice(s.lastIndexOf(dec) + 1) : '';
    var n = parseFloat(entero.replace(/[.,]/g, '') + (decimales ? '.' + decimales : ''));
    return isFinite(n) ? n : null;
}

/** Código ISO de la divisa que se ve ('EUR' | 'USD' | 'GBP'), o '' si no se ve ninguna. */
export function divisaDePrecio(texto) {
    if (esSoloNumero(texto)) return MONEDAS[DIVISA_POR_DEFECTO];
    var s = simboloDePrecio(texto);
    return s ? (MONEDAS[s] || '') : '';
}

/**
 * Lo que necesita el render: el sufijo que se pinta fuera del campo ('' o '€') y la Offer de la
 * microdata ({precio, divisa}) o null si no hay cifra o no se ve divisa.
 */
export function precioDePlan(texto) {
    var sufijo = esSoloNumero(texto) ? DIVISA_POR_DEFECTO : '';
    var n = numeroDePrecio(texto);
    var divisa = divisaDePrecio(texto);
    var oferta = (n !== null && divisa) ? { precio: (n === Math.floor(n) ? String(n) : n.toFixed(2)), divisa: divisa } : null;
    return { sufijo: sufijo, oferta: oferta };
}
